// Module: CartScreen
// Role: Cart-step screenshot brick with article confirmation prompt.
//       Extends ScreenshotBrick pattern with a product-confirm card.
// Uses:
//   - hooks/useFileUpload:useFileUpload
//   - api/flow-client:useArtefactPresign, useSubmitCheckpoint
//   - shared/limits:MAX_UGC_PHOTO_SIZE_BYTES
//   - providers/PlatformProvider:PlatformContext
//   - atoms: Frame, IOSStatusBar, MiniHeader, StepStrip, InstructionCard, CtaBar, HintBar
// Used by: FlowScreen.tsx (kind='CART')
//
// FTR-1135: Creator confirms the correct product is in cart before upload.
// ADR-023: no platform literals.

import { useContext, useRef, useState, useCallback } from 'react';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { PlatformContext } from '../../../providers/PlatformProvider';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useArtefactPresign, useSubmitCheckpoint } from '../../../api/flow-client';
import { MAX_PHOTO_INPUT_BYTES, FLOW_PHOTO_MAX_DIM } from '../../../shared/limits';
import { resizeImageToBlob } from '../../../lib/imageResize';
import { apiFetch } from '../../../api/client';
import { P, S, T } from '../../../styles/tokens';
import { flowStepLabel } from '../_labels';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

// ─── Types ───────────────────────────────────────────────────────────────────

interface CartScreenProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CartScreen({
  checkpoint,
  flowRun,
  checkpoints,
}: CartScreenProps) {
  const platform = useContext(PlatformContext);
  const canUpload = platform?.capabilities.fileUpload ?? false;

  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [article, setArticle] = useState('');

  const upload = useFileUpload();
  const presign = useArtefactPresign(flowRun.slot_id);
  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const parentCount = checkpoints.filter(
    (c) => c.parent_checkpoint_id === null,
  ).length;

  const handlePickFile = useCallback(() => {
    if (!article.trim()) {
      setHintMessage('Сначала введите артикул товара.');
      return;
    }
    if (!canUpload) {
      setHintMessage('Загрузка файлов недоступна на этой платформе.');
      return;
    }
    fileInputRef.current?.click();
  }, [article, canUpload]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      // Reset input value so re-picking the same file triggers onChange (BUG-1158 fix B)
      if (e.target) e.target.value = '';
      if (!file) return;

      // BUG-1304: 50 MB input guard
      if (file.size > MAX_PHOTO_INPUT_BYTES) {
        setHintMessage('Файл слишком большой. Максимум 50 МБ.');
        return;
      }

      setHintMessage(null);

      // BUG-1304: normalize to JPEG (money-path: cart screenshot)
      let uploadFile = file;
      try {
        const blob = await resizeImageToBlob(file, { maxDim: FLOW_PHOTO_MAX_DIM, quality: 0.85 });
        uploadFile = new File([blob], 'upload.jpg', { type: 'image/jpeg' });
      } catch (resizeErr) {
        console.warn('[BUG-1304] re-encode failed, uploading original:', resizeErr);
        void apiFetch(`/api/v2/buyer/slots/${flowRun.slot_id}/upload-error`, {
          method: 'POST',
          body: JSON.stringify({
            checkpoint_slug: checkpoint.slug,
            error_kind: 'resize_failed',
            original_mime: file.type || null,
          }),
        }).catch(() => {});
      }

      try {
        const { upload_url, artefact_path } = await presign.mutateAsync({
          checkpoint_slug: checkpoint.slug,
          artefact_kind: 'screenshot',
          content_md5: '',
          file_size_bytes: uploadFile.size,
        });

        await upload.start({ url: upload_url, file: uploadFile });

        const url = URL.createObjectURL(uploadFile);
        setThumbUrl(url);

        await submitCheckpoint.mutateAsync({
          artefact_path,
          artefacts: [
            { kind: 'screenshot', artefact_path },
            { kind: 'text', value: article.trim() },
          ],
        });
        setIsDone(true);
        platform?.hapticImpact('medium');
      } catch {
        setThumbUrl(null);
        setHintMessage('Ошибка загрузки. Попробуйте ещё раз.');
        // BUG-1304: beacon
        void apiFetch(`/api/v2/buyer/slots/${flowRun.slot_id}/upload-error`, {
          method: 'POST',
          body: JSON.stringify({
            checkpoint_slug: checkpoint.slug,
            error_kind: 'upload_failed',
            original_mime: file.type || null,
          }),
        }).catch(() => {});
      }
    },
    [article, presign, upload, submitCheckpoint, checkpoint.slug, platform],
  );

  const ctaLabel = isDone ? 'Готово' : 'Загрузить скриншот корзины';
  const isUploading = upload.status === 'uploading';

  return (
    <Frame>
      <MiniHeader title={flowStepLabel(checkpoint.kind)} />

      <div style={{ padding: '12px 16px 0' }}>
        <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
      </div>

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <InstructionCard>
          {checkpoint.instructions_text && (
            <p style={{ font: T.title3, color: S.label, margin: '0 0 12px', lineHeight: 1.2 }}>
              {checkpoint.instructions_text}
            </p>
          )}

          {/* Product confirmation card */}
          <div
            style={{
              padding: '12px 16px',
              borderRadius: 12,
              background: `rgba(226,114,91,0.08)`,
              border: `1px solid rgba(226,114,91,0.2)`,
              marginBottom: 12,
            }}
          >
            <p style={{ font: T.footnote, color: P.terracotta, margin: 0, fontWeight: 600 }}>
              Убедитесь, что в корзине именно наш товар
            </p>
            <p style={{ font: T.caption, color: S.secondaryLabel, margin: '4px 0 0' }}>
              Скриншот должен показывать товар из задания в корзине
            </p>
          </div>

          {checkpoint.step_context?.search_query && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 12,
                background: `rgba(226,114,91,0.08)`,
                border: `1px solid rgba(226,114,91,0.2)`,
              }}
            >
              <div style={{ font: T.caption, color: S.secondaryLabel, margin: 0 }}>Что искать:</div>
              <div style={{ font: T.subhead, color: S.label, fontWeight: 600, margin: '2px 0 0' }}>
                {checkpoint.step_context.search_query}
              </div>
            </div>
          )}

          <label style={{ display: 'block', marginTop: 12 }}>
            <div style={{ font: T.caption, color: S.secondaryLabel, marginBottom: 6 }}>
              Артикул товара
            </div>
            <input
              value={article}
              onChange={(e) => {
                setArticle(e.target.value);
                if (hintMessage) setHintMessage(null);
              }}
              placeholder="Например: 123456789"
              inputMode="text"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: 12,
                border: `1px solid ${P.line}`,
                background: P.card,
                color: S.label,
                font: T.callout,
                padding: '12px 14px',
                outline: 'none',
              }}
            />
          </label>
        </InstructionCard>

        {/* Thumbnail preview */}
        {thumbUrl && (
          <div style={{ borderRadius: 12, overflow: 'hidden', maxHeight: 200 }}>
            <img
              src={thumbUrl}
              alt="Скриншот корзины"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {hintMessage && <HintBar kind="warn">{hintMessage}</HintBar>}

      <WizardCtaBar
        label={ctaLabel}
        onClick={isDone ? () => {} : handlePickFile}
        disabled={isDone || isUploading || !article.trim()}
      />
    </Frame>
  );
}
